import React, { useMemo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { LinearProgress } from '@material-ui/core';
import { isArray } from 'lodash';
import moment from 'moment';
import qs from 'querystring';
import * as Yup from 'yup';

import { t, can } from '~/helpers';
import { visualizarCO, generateReport } from '~/helpers/reportUtils';
import api from '~/services/api';

import { Header, Table } from './components';
import * as S from './styles';

import { MODULO_CUSTOMIZACAO_RUMO } from '~/constants/modules';

//
function RelPitStopTempos() {
  const objToken = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [wherefield, setWherefield] = useState();
  const [valuewherefield, setValuewherefield] = useState();
  const [typewherefield, setTypewherefield] = useState();
  const [filteruser, setFilteruser] = useState();
  const [textoPesquisa, setTextoPesquisa] = useState('');

  const header = useMemo(() => {
    const agora = moment();

    return {
      datainicio: agora
        .clone()
        .subtract(7, 'day')
        .format('DD/MM/YYYY 00:00:00'),
      datafim: agora.clone().format('DD/MM/YYYY 23:59:59'),
      idbico: 0,
      idveiculo: '',
      codigoatendimento: '',
    };
  }, []);

  const schema = useMemo(() =>
    Yup.object().shape({
      datainicio: Yup.mixed()
        .transform((value) =>
          value ? moment(value, 'DD/MM/YYYY HH:mm:ss') : null
        )
        .required(t('s.geral.validacao.campoobrigatorio')),
      datafim: Yup.mixed()
        .transform((value) =>
          value ? moment(value, 'DD/MM/YYYY HH:mm:ss') : null
        )
        .required(t('s.geral.validacao.campoobrigatorio'))
        .test(
          'datafimmaior',
          t('s.operacao.descarganf.datahorainicialmaiordatahorafinal'),
          function dataFimMaior(value) {
            const { datainicio } = this.parent;
            return value && datainicio ? value.isAfter(datainicio) : true;
          }
        ),
      idveiculo: Yup.string(),
      codigoatendimento: Yup.number()
        .transform((value, originalValue) =>
          String(originalValue).trim() === '' ? null : value
        )
        .nullable(),
      posto: Yup.array().of(Yup.object()).nullable(),
      idbico: Yup.string().nullable(),
    })
  );

  const handleSubmitHeader = useCallback(async (formData) => {
    try {
      setLoading(true);

      await schema.validate(formData, {
        abortEarly: false,
      });

      const {
        datainicio,
        datafim,
        idveiculo,
        codigoatendimento,
        posto,
        idbico,
      } = formData;

      const datahorainicio = moment(datainicio, 'DD/MM/YYYY HH:mm:ss').format(
        'YYYY-MM-DDTHH:mm:ss'
      );

      const datahorafim = moment(datafim, 'DD/MM/YYYY HH:mm:ss').format(
        'YYYY-MM-DDTHH:mm:ss'
      );

      const where = [
        'dataencosteatendimento',
        'dataliberacaoatendimento',
        'lubrificacaooficinaatendimento',
        'sempitstopatendimento',
      ];
      const valuewhere = [datahorainicio, datahorafim, 'N', 'N'];
      const typewhere = ['MAIOR_IGUAL', 'MENOR_IGUAL', 'EQUALS', 'EQUALS'];
      let filterusr = false;
      let filtro =
        `FILTRO - ${t('s.geral.opcoes.datahorainicial')}: ${datainicio} | ` +
        `${t('s.geral.opcoes.datahorafinal')}: ${datafim}`;

      if (idveiculo && idveiculo.length > 0) {
        where.push('idveiculo');
        valuewhere.push(idveiculo);
        typewhere.push('EQUALS');

        filtro += ` | ${t('s.menu.cadastro.veiculo.locomotiva')}: ${idveiculo}`;
      }

      if (codigoatendimento && codigoatendimento.length > 0) {
        where.push('codigoatendimento');
        valuewhere.push(codigoatendimento);
        typewhere.push('EQUALS');

        filtro += ` | ${t(
          's.cadastro.pitstop.codigoatendimento'
        )}: ${codigoatendimento}`;
      }

      if (!posto || posto.length === 0) {
        filterusr = true;
      } else if (posto.length === 1) {
        where.push('idposto');
        valuewhere.push(posto[0].idposto);
        typewhere.push('EQUALS');

        const { codigonomeposto } = posto[0];

        filtro += ` | ${t('s.geral.postocomboio')}: ${codigonomeposto}`;

        if (idbico && idbico > 0) {
          where.push('idbicoabastecimento');
          valuewhere.push(idbico);
          typewhere.push('EQUALS');

          filtro += ` | ${t('s.menu.configuracao.bico')}: ${idbico}`;
        }
      } else {
        where.push('idposto');
        valuewhere.push(posto.map((p) => p.idposto).join(','));
        typewhere.push('IN');

        const postosNomes = posto.map((p) => p.codigonomeposto).join(', ');

        filtro += ` | ${t('s.geral.postocomboio')}: ${postosNomes}`;
      }

      setWherefield(where);
      setTypewherefield(typewhere);
      setValuewherefield(valuewhere);
      setFilteruser(filterusr);
      setTextoPesquisa(filtro);

      const params = {
        fieldlist:
          'codigonomeposto, codigoatendimento, idbicoabastecimento, ' +
          'idveiculo, numeroturno, dataencosteatendimento, ' +
          'dataliberacaoatendimento, duracaoatendimentodate, ' +
          'duracaoabastecimento, ' +
          (can(MODULO_CUSTOMIZACAO_RUMO) ? 'duracaobanheiro' : 'duracaoagua') +
          ', duracaoareia, duracaolimpeza, duracaolubrificacao, iplatendimento',
        wherefield: where,
        typewherefield: typewhere,
        valuewherefield: valuewhere,
        filteruser: filterusr,
        orderfield: 'dataencosteatendimento',
        ordination: 'asc',
        pagesize: 0,
      };

      const { data } = await api.get('pitstop', {
        params,
        paramsSerializer: (param) => qs.stringify(param),
      });

      setDataTable(data.items);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          if (error?.message) toast.error(error.message);
        });
      } else if (isArray(err)) {
        err.map((e) => toast.error(t(e)));
      } else if (typeof err === 'string') {
        toast.error(t(err));
      } else {
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  });

  const handleExportData = useCallback(
    async (formato) => {
      try {
        setLoading(true);

        // Objeto de parâmetros completo que será passado para generateReport
        const reportRequestParams = {
          fieldlist:
            'codigonomeposto, codigoatendimento, idbicoabastecimento, ' +
            'idveiculo, numeroturno, dataencosteatendimento, ' +
            'dataliberacaoatendimento, duracaoatendimentodate, ' +
            'duracaoabastecimento, ' +
            (can(MODULO_CUSTOMIZACAO_RUMO)
              ? 'duracaobanheiro'
              : 'duracaoagua') +
            ', duracaoareia, duracaolimpeza, duracaolubrificacao, iplatendimento',
          wherefield,
          typewherefield,
          valuewherefield,
          filteruser,
          orderfield: 'dataencosteatendimento',
          ordination: 'asc',
          relatorio: {
            custom: true,
            formato,
            ignorarFormatacao: JSON.stringify({
              codigoatendimento: true,
            }),
            tituloRelatorio: t('s.menu.relatorio.pitstoptempos'),
            substituirHeader: {
              idveiculo: t('s.menu.cadastro.veiculo.locomotiva'),
              dataencosteatendimento: t('s.geral.opcoes.datahorainicial'),
              dataliberacaoatendimento: t('s.geral.opcoes.datahorafinal'),
            },
            textoPesquisa,
          },
        };

        await generateReport({
          endpoint: `pitstop`,
          params: reportRequestParams,
        });
      } catch (err) {
        if (Array.isArray(err)) {
          err.map((e) => toast.error(t(e)));
        } else if (typeof err === 'string') {
          toast.error(t(err));
        } else {
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      wherefield,
      typewherefield,
      valuewherefield,
      filteruser,
      textoPesquisa,
      objToken.token,
      toast,
      t,
    ]
  );

  const handleExibeCO = (idatendimento) => {
    visualizarCO(idatendimento, objToken.token);
  };

  return (
    <S.Wrapper>
      {loading && <LinearProgress />}
      <Header
        data={header}
        loading={loading}
        handleSubmit={handleSubmitHeader}
        canExport={dataTable.length > 0}
        onExportClick={handleExportData}
      />

      <Table rows={dataTable} onIconClick={handleExibeCO} />
    </S.Wrapper>
  );
}

export default RelPitStopTempos;
